import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

const INDEXED_COLORS = {
  pending:  { bg: '#fef9c3', color: '#854d0e', label: 'Not Indexed' },
  indexing: { bg: '#dbeafe', color: '#1e40af', label: 'Indexing…'  },
  indexed:  { bg: '#f0fdf4', color: '#166534', label: 'Indexed'    },
  failed:   { bg: '#fef2f2', color: '#991b1b', label: 'Index Failed'},
};

const SuperAdminEventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Matching trigger
  const [threshold, setThreshold] = useState(35);
  const [triggering, setTriggering] = useState(false);
  const [triggerError, setTriggerError] = useState('');
  const [matchStatus, setMatchStatus] = useState(null);
  const pollRef = useRef(null);

  // Publish
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState('');
  const [publishError, setPublishError] = useState('');

  // Gallery URLs
  const [galleryUrls, setGalleryUrls] = useState(null);
  const [loadingUrls, setLoadingUrls] = useState(false);
  const [copied, setCopied] = useState(null); // visitor id that was just copied

  const loadEvent = useCallback(() => {
    return axios.get(`${API_URL}/api/superadmin/events/${id}`)
      .then(res => setEvent(res.data))
      .catch(err => { if (err.response?.status === 404) navigate('/admin/super'); });
  }, [id, navigate]);

  const loadMatchStatus = useCallback(() => {
    return axios.get(`${API_URL}/api/superadmin/events/${id}/match-status`)
      .then(res => setMatchStatus(res.data));
  }, [id]);

  useEffect(() => {
    Promise.all([loadEvent(), loadMatchStatus()]).finally(() => setLoading(false));
  }, [loadEvent, loadMatchStatus]);

  // Poll match status while running
  useEffect(() => {
    if (matchStatus?.status === 'running') {
      pollRef.current = setInterval(() => {
        loadMatchStatus().then(() => {
          if (matchStatus?.status !== 'running') {
            clearInterval(pollRef.current);
            loadEvent(); // refresh event status
          }
        });
      }, 3000);
    } else {
      clearInterval(pollRef.current);
    }
    return () => clearInterval(pollRef.current);
  }, [matchStatus?.status, loadMatchStatus, loadEvent]);

  const triggerMatch = async () => {
    setTriggerError('');
    setTriggering(true);
    try {
      await axios.post(`${API_URL}/api/superadmin/events/${id}/trigger-match`, {
        threshold: threshold / 100
      });
      await loadMatchStatus();
    } catch (err) {
      setTriggerError(err.response?.data?.error || 'Failed to start matching.');
    } finally {
      setTriggering(false);
    }
  };

  const publishEvent = async () => {
    setPublishError('');
    setPublishing(true);
    try {
      const res = await axios.post(`${API_URL}/api/superadmin/events/${id}/publish`);
      setPublishResult(res.data.message);
      loadEvent();
      loadGalleryUrls();
    } catch (err) {
      setPublishError(err.response?.data?.error || 'Publish failed.');
    } finally {
      setPublishing(false);
    }
  };

  const loadGalleryUrls = () => {
    setLoadingUrls(true);
    axios.get(`${API_URL}/api/superadmin/events/${id}/gallery-urls`)
      .then(res => setGalleryUrls(res.data))
      .catch(console.error)
      .finally(() => setLoadingUrls(false));
  };

  const copyUrl = (url, visitorId) => {
    navigator.clipboard.writeText(url);
    setCopied(visitorId);
    setTimeout(() => setCopied(null), 2000);
  };

  const copyAllUrls = () => {
    if (!galleryUrls) return;
    const text = galleryUrls.map(v => `${v.name}\t${v.mobile}\t${v.gallery_url}`).join('\n');
    navigator.clipboard.writeText(text);
    alert(`Copied ${galleryUrls.length} gallery URLs to clipboard.`);
  };

  if (loading) return <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>Loading…</div>;
  if (!event) return null;

  const ic = INDEXED_COLORS[event.indexed_status] || INDEXED_COLORS.pending;
  const isRunning   = matchStatus?.status === 'running';
  const isCompleted = matchStatus?.status === 'completed';
  const isPublished = !!event.published_at;
  const canMatch    = (event.indexed_status === 'indexed' || event.indexed_status === 'indexing') && !isRunning;
  const canPublish  = isCompleted && !isPublished;

  return (
    <div style={styles.page}>
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', borderBottom: '1px solid #e5e7eb', background: '#fff' }}>
        <button style={styles.backBtn} onClick={() => navigate('/admin/super')}>← Dashboard</button>
        <span style={{ fontWeight: 700, color: '#1a1a2e' }}>{event.name}</span>
        {isPublished && <span style={styles.publishedBadge}>✦ Published</span>}
        <button style={{ ...styles.refreshBtn, marginLeft: 'auto' }} onClick={() => navigate(`/admin/events/${id}/browser?from=super`)}>
          Browse Photos
        </button>
      </div>

      <div style={styles.container}>

        {/* Event Info */}
        <div style={styles.infoGrid}>
          <InfoCard label="Photographer" value={event.photographer_name} />
          <InfoCard label="Date" value={new Date(event.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })} />
          <InfoCard label="Visitors" value={event.visitor_count} />
          <InfoCard label="Photos" value={event.photo_count} />
          <InfoCard label="Indexed Faces" value={event.indexed_faces} />
        </div>

        {/* ── Indexing Status ─────────────────────────────────────────────────── */}
        <Section title="Indexing Status">
          <div style={styles.indexRow}>
            <span style={{ ...styles.badge, background: ic.bg, color: ic.color, fontSize: '0.9rem', padding: '0.35rem 0.875rem' }}>
              {ic.label}
            </span>
            <span style={styles.indexNote}>
              {event.indexed_status === 'pending'  && 'Run match_v2_index.py on your local machine to index this event\'s photos.'}
              {event.indexed_status === 'indexing' && 'Indexing is in progress on the local machine. Refresh to check status.'}
              {event.indexed_status === 'indexed'  && `${event.indexed_faces} face embeddings ready. You can now run matching.`}
              {event.indexed_status === 'failed'   && 'Indexing failed. Re-run match_v2_index.py locally.'}
            </span>
            <button style={styles.refreshBtn} onClick={() => loadEvent()}>↻ Refresh</button>
          </div>
          {event.indexed_at && (
            <p style={styles.indexedAt}>Indexed at: {new Date(event.indexed_at).toLocaleString('en-IN')}</p>
          )}
        </Section>

        {/* ── Matching ────────────────────────────────────────────────────────── */}
        <Section title="Face Matching">
          {event.indexed_status === 'pending' || event.indexed_status === 'failed' ? (
            <p style={styles.grayNote}>Matching is available after photos are indexed.</p>
          ) : (
            <>
              {/* Threshold slider + Run button — always visible when matching is possible */}
              {canMatch && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={styles.thresholdRow}>
                    <label style={styles.thresholdLabel}>
                      Similarity Threshold: <strong>{threshold}%</strong>
                    </label>
                    <input
                      type="range" min={20} max={80} value={threshold}
                      onChange={e => setThreshold(Number(e.target.value))}
                      style={{ width: '220px', accentColor: '#4f46e5' }}
                    />
                    <span style={styles.thresholdHint}>
                      {threshold < 30 ? 'Very loose — many matches, possible false positives' :
                       threshold < 45 ? 'Balanced — recommended starting point' :
                       threshold < 60 ? 'Strict — fewer but high-confidence matches' :
                       'Very strict — only near-identical faces'}
                    </span>
                  </div>
                  <button
                    style={{ ...styles.primaryBtn, ...(triggering ? styles.disabledBtn : {}) }}
                    onClick={triggerMatch}
                    disabled={triggering}
                  >
                    {triggering ? 'Starting…' : isCompleted ? '▶ Re-run Matching' : '▶ Run Matching'}
                  </button>
                </div>
              )}
              {triggerError && <p style={styles.errorMsg}>{triggerError}</p>}

              {/* Progress */}
              {isRunning && matchStatus && (
                <div style={styles.progressBox}>
                  <p style={styles.progressLabel}>
                    Matching in progress… {matchStatus.processed_photos} / {matchStatus.total_photos} photos
                    &nbsp;({matchStatus.progress_percent}%)
                  </p>
                  <div style={styles.progressTrack}>
                    <div style={{ ...styles.progressFill, width: `${matchStatus.progress_percent}%` }} />
                  </div>
                  <p style={styles.progressMatches}>{matchStatus.total_matches ?? 0} matches found so far</p>
                </div>
              )}

              {/* Completed result */}
              {isCompleted && matchStatus && (
                <div style={styles.completedBox}>
                  <span style={{ fontSize: '1.5rem' }}>✅</span>
                  <div>
                    <p style={{ margin: 0, fontWeight: '700', color: '#166534' }}>Matching complete</p>
                    <p style={{ margin: '0.2rem 0 0', color: '#555', fontSize: '0.875rem' }}>
                      {matchStatus.total_matches} matches across {matchStatus.total_photos} photos
                      · threshold {Math.round(matchStatus.threshold * 100)}%
                      · completed {new Date(matchStatus.completed_at).toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>
              )}

              {/* Failed */}
              {matchStatus?.status === 'failed' && (
                <p style={styles.errorMsg}>Matching failed: {matchStatus.error_message}</p>
              )}
            </>
          )}
        </Section>

        {/* ── Publish ─────────────────────────────────────────────────────────── */}
        <Section title="Publish Event">
          {isPublished ? (
            <div style={styles.completedBox}>
              <span style={{ fontSize: '1.5rem' }}>✦</span>
              <div>
                <p style={{ margin: 0, fontWeight: '700', color: '#5b21b6' }}>Event is published</p>
                <p style={{ margin: '0.2rem 0 0', color: '#555', fontSize: '0.875rem' }}>
                  Published {new Date(event.published_at).toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          ) : canPublish ? (
            <div>
              <p style={styles.grayNote}>
                Publishing generates a unique gallery URL for every visitor. Share these links with visitors via WhatsApp or any channel.
              </p>
              <button
                style={{ ...styles.primaryBtn, background: '#5b21b6', ...(publishing ? styles.disabledBtn : {}) }}
                onClick={publishEvent}
                disabled={publishing}
              >
                {publishing ? 'Publishing…' : '✦ Publish Event & Generate Gallery URLs'}
              </button>
              {publishError && <p style={styles.errorMsg}>{publishError}</p>}
            </div>
          ) : (
            <p style={styles.grayNote}>
              {!isCompleted
                ? 'Run and complete matching before publishing.'
                : 'Event is ready to publish.'}
            </p>
          )}
          {publishResult && <p style={styles.successMsg}>{publishResult}</p>}
        </Section>

        {/* ── Gallery URLs ─────────────────────────────────────────────────────── */}
        {isPublished && (
          <Section title="Visitor Gallery URLs">
            {!galleryUrls ? (
              <button style={styles.secondaryBtn} onClick={loadGalleryUrls} disabled={loadingUrls}>
                {loadingUrls ? 'Loading…' : '📋 Load Gallery URLs'}
              </button>
            ) : (
              <>
                <div style={styles.urlsHeader}>
                  <p style={styles.urlsCount}>{galleryUrls.length} visitors with gallery links</p>
                  <button style={styles.secondaryBtn} onClick={copyAllUrls}>
                    Copy All (Name + Mobile + URL)
                  </button>
                </div>
                <div style={styles.urlsTableWrap}>
                  <table style={styles.table}>
                    <thead>
                      <tr style={styles.thead}>
                        <Th>Name</Th><Th>Mobile</Th><Th>Matches</Th><Th>Gallery URL</Th><Th></Th>
                      </tr>
                    </thead>
                    <tbody>
                      {galleryUrls.map(v => (
                        <tr key={v.id} style={styles.tr}>
                          <Td>{v.name}</Td>
                          <Td>{v.mobile}</Td>
                          <Td><span style={{ fontWeight: '700', color: v.match_count > 0 ? '#166534' : '#888' }}>{v.match_count}</span></Td>
                          <Td>
                            <span style={styles.urlText}>{v.gallery_url}</span>
                          </Td>
                          <Td>
                            <button
                              style={{ ...styles.copyBtn, ...(copied === v.id ? styles.copiedBtn : {}) }}
                              onClick={() => copyUrl(v.gallery_url, v.id)}
                            >
                              {copied === v.id ? '✓ Copied' : 'Copy'}
                            </button>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Section>
        )}

      </div>
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const Section = ({ title, children }) => (
  <div style={styles.section}>
    <h2 style={styles.sectionTitle}>{title}</h2>
    {children}
  </div>
);

const InfoCard = ({ label, value }) => (
  <div style={styles.infoCard}>
    <div style={styles.infoValue}>{value}</div>
    <div style={styles.infoLabel}>{label}</div>
  </div>
);

const Th = ({ children }) => <th style={styles.th}>{children}</th>;
const Td = ({ children }) => <td style={styles.td}>{children}</td>;

const styles = {
  page: { minHeight: '100vh', background: '#f8fafc', fontFamily: "'Segoe UI', system-ui, sans-serif" },
  nav: { background: '#1a1a2e', padding: '1rem 2rem', display: 'flex', alignItems: 'center', gap: '1rem' },
  backBtn: { background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.4rem 0.875rem', cursor: 'pointer', fontSize: '0.875rem' },
  navTitle: { color: '#fff', fontWeight: '700', flex: 1, fontSize: '1.05rem' },
  publishedBadge: { background: '#f5f3ff', color: '#5b21b6', borderRadius: '50px', padding: '0.3rem 0.875rem', fontSize: '0.8rem', fontWeight: '700' },
  container: { maxWidth: '1000px', margin: '0 auto', padding: '2rem 1.5rem' },

  infoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' },
  infoCard: { background: '#fff', borderRadius: '1rem', padding: '1rem 1.25rem', border: '1px solid #f0f0f0', textAlign: 'center' },
  infoValue: { fontSize: '1.5rem', fontWeight: '800', color: '#1a1a2e', lineHeight: 1.2 },
  infoLabel: { fontSize: '0.75rem', color: '#888', marginTop: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' },

  section: { background: '#fff', borderRadius: '1.25rem', padding: '1.5rem', marginBottom: '1.25rem', border: '1px solid #f0f0f0' },
  sectionTitle: { margin: '0 0 1.25rem', fontSize: '1rem', fontWeight: '800', color: '#1a1a2e', textTransform: 'uppercase', letterSpacing: '0.05em' },

  indexRow: { display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' },
  indexNote: { flex: 1, fontSize: '0.875rem', color: '#555', minWidth: '200px' },
  indexedAt: { margin: '0.75rem 0 0', fontSize: '0.8rem', color: '#aaa' },
  refreshBtn: { background: '#f0f4ff', color: '#4f46e5', border: 'none', borderRadius: '0.5rem', padding: '0.4rem 0.875rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem' },
  badge: { display: 'inline-block', borderRadius: '50px', fontWeight: '700', whiteSpace: 'nowrap' },

  thresholdRow: { display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' },
  thresholdLabel: { fontSize: '0.875rem', color: '#444', minWidth: '200px' },
  thresholdHint: { fontSize: '0.8rem', color: '#888' },
  grayNote: { color: '#888', fontSize: '0.875rem', margin: '0 0 1rem' },

  primaryBtn: { padding: '0.75rem 1.5rem', background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '0.75rem', fontWeight: '700', cursor: 'pointer', fontSize: '0.95rem' },
  secondaryBtn: { padding: '0.5rem 1rem', background: '#f0f4ff', color: '#4f46e5', border: 'none', borderRadius: '0.5rem', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem' },
  disabledBtn: { opacity: 0.5, cursor: 'not-allowed' },

  progressBox: { background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '0.75rem', padding: '1rem', marginTop: '1rem' },
  progressLabel: { margin: '0 0 0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#1a1a2e' },
  progressTrack: { height: '10px', background: '#e5e7eb', borderRadius: '99px', overflow: 'hidden' },
  progressFill: { height: '100%', background: '#4f46e5', borderRadius: '99px', transition: 'width 0.5s ease' },
  progressMatches: { margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#888' },

  completedBox: { display: 'flex', alignItems: 'center', gap: '1rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.75rem', padding: '1rem' },
  errorMsg: { color: '#dc2626', fontSize: '0.875rem', margin: '0.75rem 0 0' },
  successMsg: { color: '#166534', fontSize: '0.875rem', margin: '0.75rem 0 0', fontWeight: '600' },

  urlsHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' },
  urlsCount: { margin: 0, fontSize: '0.875rem', color: '#555' },
  urlsTableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '600px' },
  thead: { background: '#f8fafc' },
  th: { padding: '0.6rem 0.875rem', textAlign: 'left', fontSize: '0.72rem', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f5f5f5' },
  td: { padding: '0.7rem 0.875rem', fontSize: '0.85rem', color: '#444' },
  urlText: { fontSize: '0.75rem', color: '#4f46e5', wordBreak: 'break-all' },
  copyBtn: { padding: '0.3rem 0.75rem', background: '#f0f4ff', color: '#4f46e5', border: 'none', borderRadius: '0.4rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' },
  copiedBtn: { background: '#f0fdf4', color: '#166534' },
};

export default SuperAdminEventDetail;
